/**
 * ChatWidget — CHI Alliance AI Assistant
 * Obsidian Pulse design: bottom-right persistent widget.
 * Powered by the server-side /api/chat endpoint; no provider credential reaches the browser.
 */
import { useState, useRef, useEffect, useCallback } from "react";
import gsap from "gsap";
import { X, Send, MessageCircle, Loader2, Bot } from "lucide-react";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const SUGGESTED_QUESTIONS = [
  "What events are coming up?",
  "Who are the featured speakers?",
  "How can I become a member?",
  "What partnership opportunities exist?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);
  const fabRef = useRef<HTMLButtonElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasGreeted = useRef(false);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Greeting on first open
  useEffect(() => {
    if (open && !hasGreeted.current) {
      hasGreeted.current = true;
      setMessages([
        {
          role: "assistant",
          content:
            "Hi! I'm the CHI Alliance AI Assistant. I can help you with information about our events, speakers, membership, and partnership opportunities. What would you like to know?",
        },
      ]);
    }
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  // GSAP open animation
  const openPanel = useCallback(() => {
    setOpen(true);
    requestAnimationFrame(() => {
      if (!panelRef.current) return;
      gsap.fromTo(
        panelRef.current,
        { opacity: 0, scale: 0.88, y: 20, transformOrigin: "bottom right" },
        { opacity: 1, scale: 1, y: 0, duration: 0.32, ease: "back.out(1.4)" }
      );
    });
  }, []);

  // GSAP close animation
  const closePanel = useCallback(() => {
    if (!panelRef.current) return;
    gsap.to(panelRef.current, {
      opacity: 0,
      scale: 0.88,
      y: 16,
      duration: 0.22,
      ease: "back.in(1.4)",
      onComplete: () => setOpen(false),
    });
  }, []);

  // FAB pulse on mount
  useEffect(() => {
    if (!fabRef.current) return;
    gsap.fromTo(
      fabRef.current,
      { scale: 0.6, opacity: 0 },
      { scale: 1, opacity: 1, duration: 0.45, ease: "back.out(1.7)", delay: 1.2 }
    );
  }, []);

  const sendMessage = useCallback(
    async (text?: string) => {
      const content = (text ?? input).trim();
      if (!content || loading) return;
      setInput("");

      const userMsg: Message = { role: "user", content };
      const nextMessages = [...messages, userMsg];
      setMessages(nextMessages);
      setLoading(true);

      try {
        const res = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            messages: nextMessages.map((m) => ({ role: m.role, content: m.content })),
          }),
        });
        if (!res.ok) {
          setMessages((prev) => [
            ...prev,
            {
              role: "assistant",
              content:
                res.status === 429
                  ? "I’m receiving a lot of questions right now. Please try again in a moment."
                  : "I’m having trouble connecting right now. Please try again in a moment.",
            },
          ]);
          return;
        }

        const data = await res.json() as { reply?: unknown };
        if (typeof data.reply !== "string" || !data.reply.trim()) {
          throw new Error("The assistant returned an empty response");
        }
        const reply = data.reply;
        setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
      } catch {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "I'm having trouble connecting right now. Please try again in a moment." },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [input, messages, loading]
  );

  return (
    <div
      className="chi-chat-launcher"
      style={{
        position: "fixed",
        bottom: "max(1rem, env(safe-area-inset-bottom))",
        right: "max(1rem, env(safe-area-inset-right))",
        zIndex: 9999,
      }}
    >

      {/* Chat panel */}
      {open && (
        <div
          ref={panelRef}
          id="chi-assistant-panel"
          role="dialog"
          aria-label="CHI Alliance Assistant"
          style={{
            position: "absolute",
            bottom: "calc(100% + 1rem)",
            right: 0,
            width: "min(380px, calc(100vw - 2rem))",
            maxHeight: "min(560px, calc(100dvh - 7rem))",
            display: "flex",
            flexDirection: "column",
            background: "rgba(10,14,28,0.97)",
            border: "1px solid rgba(233,30,140,0.25)",
            borderRadius: "1.25rem",
            boxShadow: "0 24px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04), 0 0 60px rgba(233,30,140,0.08)",
            backdropFilter: "blur(24px)",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "1rem 1.25rem",
              borderBottom: "1px solid rgba(255,255,255,0.07)",
              background: "linear-gradient(135deg, rgba(233,30,140,0.12) 0%, rgba(0,212,255,0.06) 100%)",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #E91E8C, #F5A623)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 0 16px rgba(233,30,140,0.4)",
              }}
            >
              <img
                src="/media/brand/chi-flower-symbol.png"
                alt="CHI"
                style={{ width: 120, height: "auto", objectFit: "contain" }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  color: "white",
                  lineHeight: 1.2,
                }}
              >
                CHI Alliance Assistant
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.375rem",
                  marginTop: "0.15rem",
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#22C55E",
                    boxShadow: "0 0 6px #22C55E",
                    display: "inline-block",
                  }}
                />
                <span
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "0.7rem",
                    color: "#64748B",
                  }}
                >
                  Online · Powered by AI
                </span>
              </div>
            </div>
            <button
              type="button"
              aria-label="Close CHI Alliance Assistant"
              onClick={closePanel}
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#64748B",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.15s ease",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(233,30,140,0.15)";
                e.currentTarget.style.color = "#E91E8C";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                e.currentTarget.style.color = "#64748B";
              }}
            >
              <X size={13} />
            </button>
          </div>

          {/* Messages */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "1rem 1.25rem",
              display: "flex",
              flexDirection: "column",
              gap: "0.875rem",
              minHeight: 0,
            }}
          >
            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                  gap: "0.5rem",
                  alignItems: "flex-end",
                }}
              >
                {msg.role === "assistant" && (
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #E91E8C22, #F5A62322)",
                      border: "1px solid rgba(233,30,140,0.3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Bot size={13} style={{ color: "#E91E8C" }} />
                  </div>
                )}
                <div
                  style={{
                    maxWidth: "78%",
                    padding: "0.625rem 0.875rem",
                    borderRadius:
                      msg.role === "user"
                        ? "1rem 1rem 0.25rem 1rem"
                        : "1rem 1rem 1rem 0.25rem",
                    background:
                      msg.role === "user"
                        ? "linear-gradient(135deg, #E91E8C, #C2185B)"
                        : "rgba(255,255,255,0.05)",
                    border:
                      msg.role === "user"
                        ? "none"
                        : "1px solid rgba(255,255,255,0.08)",
                    color: "white",
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "var(--chi-body-compact)",
                    lineHeight: 1.6,
                    boxShadow:
                      msg.role === "user"
                        ? "0 4px 16px rgba(233,30,140,0.3)"
                        : "none",
                  }}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {loading && (
              <div style={{ display: "flex", alignItems: "flex-end", gap: "0.5rem" }}>
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    background: "rgba(233,30,140,0.1)",
                    border: "1px solid rgba(233,30,140,0.3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Bot size={13} style={{ color: "#E91E8C" }} />
                </div>
                <div
                  style={{
                    padding: "0.625rem 0.875rem",
                    borderRadius: "1rem 1rem 1rem 0.25rem",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.375rem",
                  }}
                >
                  <Loader2 size={13} style={{ color: "#E91E8C", animation: "spin 1s linear infinite" }} />
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.75rem", color: "#64748B" }}>
                    Thinking…
                  </span>
                </div>
              </div>
            )}

            {/* Suggested questions — only shown when no user messages yet */}
            {messages.length === 1 && !loading && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginTop: "0.25rem" }}>
                <p
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "#475569",
                    marginBottom: "0.2rem",
                  }}
                >
                  Suggested
                </p>
                {SUGGESTED_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    onClick={() => sendMessage(q)}
                    style={{
                      textAlign: "left",
                      background: "rgba(233,30,140,0.06)",
                      border: "1px solid rgba(233,30,140,0.2)",
                      borderRadius: "0.625rem",
                      padding: "0.5rem 0.75rem",
                      color: "#CBD5E1",
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "0.78rem",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(233,30,140,0.12)";
                      e.currentTarget.style.borderColor = "rgba(233,30,140,0.4)";
                      e.currentTarget.style.color = "white";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(233,30,140,0.06)";
                      e.currentTarget.style.borderColor = "rgba(233,30,140,0.2)";
                      e.currentTarget.style.color = "#CBD5E1";
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div
            style={{
              padding: "0.875rem 1.25rem",
              borderTop: "1px solid rgba(255,255,255,0.07)",
              display: "flex",
              gap: "0.625rem",
              alignItems: "center",
              background: "rgba(255,255,255,0.02)",
              flexShrink: 0,
            }}
          >
            <input
              ref={inputRef}
              aria-label="Message for the CHI Alliance Assistant"
              maxLength={1000}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Ask about events, speakers, membership…"
              style={{
                flex: 1,
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "0.625rem",
                padding: "0.625rem 0.875rem",
                color: "white",
                fontFamily: "'Inter', sans-serif",
                fontSize: "0.825rem",
                outline: "none",
                transition: "border-color 0.15s ease",
              }}
              onFocus={(e) => (e.target.style.borderColor = "rgba(233,30,140,0.5)")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
            />
            <button
              type="button"
              aria-label="Send message"
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              style={{
                width: 36,
                height: 36,
                borderRadius: "0.625rem",
                background:
                  input.trim() && !loading
                    ? "linear-gradient(135deg, #E91E8C, #C2185B)"
                    : "rgba(255,255,255,0.06)",
                border: "none",
                color: input.trim() && !loading ? "white" : "#475569",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: input.trim() && !loading ? "pointer" : "not-allowed",
                transition: "all 0.2s ease",
                flexShrink: 0,
                boxShadow:
                  input.trim() && !loading
                    ? "0 4px 16px rgba(233,30,140,0.35)"
                    : "none",
              }}
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      )}

      {/* FAB trigger */}
      <div style={{ position: "relative" }}>
        {/* Tooltip */}
        {showTooltip && !open && (
          <div
            style={{
              position: "absolute",
              bottom: "calc(100% + 0.5rem)",
              right: 0,
              background: "rgba(10,14,28,0.95)",
              border: "1px solid rgba(233,30,140,0.3)",
              borderRadius: "0.5rem",
              padding: "0.375rem 0.75rem",
              whiteSpace: "nowrap",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "white",
              pointerEvents: "none",
              boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            }}
          >
            Ask CHI Assistant
          </div>
        )}

        <button
          ref={fabRef}
          type="button"
          aria-label={open ? "Close CHI Alliance Assistant" : "Open CHI Alliance Assistant"}
          aria-expanded={open}
          aria-controls="chi-assistant-panel"
          onClick={open ? closePanel : openPanel}
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: open
              ? "rgba(233,30,140,0.15)"
              : "linear-gradient(135deg, #E91E8C 0%, #C2185B 100%)",
            border: open
              ? "1px solid rgba(233,30,140,0.4)"
              : "none",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: open
              ? "0 0 24px rgba(233,30,140,0.25)"
              : "0 8px 32px rgba(233,30,140,0.45), 0 4px 16px rgba(0,0,0,0.4)",
            transition: "all 0.25s cubic-bezier(0.23, 1, 0.32, 1)",
            position: "relative",
            opacity: 0, // GSAP animates this in
          }}
          onMouseEnter={(e) => {
            setShowTooltip(true);
            if (!open) {
              e.currentTarget.style.transform = "scale(1.08)";
              e.currentTarget.style.boxShadow = "0 10px 40px rgba(233,30,140,0.6), 0 4px 16px rgba(0,0,0,0.4)";
            }
          }}
          onMouseLeave={(e) => {
            setShowTooltip(false);
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = open
              ? "0 0 24px rgba(233,30,140,0.25)"
              : "0 8px 32px rgba(233,30,140,0.45), 0 4px 16px rgba(0,0,0,0.4)";
          }}
        >
          {open ? <X size={20} /> : <MessageCircle size={22} />}

          {/* Unread dot — shown before first open */}
          {!hasGreeted.current && !open && (
            <span
              style={{
                position: "absolute",
                top: 6,
                right: 6,
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "#22C55E",
                border: "2px solid #080C18",
                boxShadow: "0 0 8px #22C55E",
              }}
            />
          )}
        </button>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
